import { t, defineMessage } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';
import { Role_Id } from './roleId';
import { CountryInfo } from '@core/types';

// 默认对象类型声明
export type DefaultObjectType = { [key: string]: string };

// 定义排除类型：将U从T中剔除, keyof 会取出T与U的所有键, 限定P的取值范围为 T 中的所有键, 并将其类型设为 never
export type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };

// 定义互斥类型，T 或 U 只有一个能出现（互相剔除时，被剔除方必须存在）
export type XOR<T, U> = (Without<T, U> & U) | (Without<U, T> & T);

/**
 * @desc 后端接口响应 API 字段类型
 * @param {string} code "0"-响应成功 | string-其他非成功状态状态
 * @param {string} message 响应信息
 * @param { T | undefined} [data] 响应数据，响应失败字段为 undefined | 不存在
 */
export interface ResponseAPIType<T = undefined> {
    code: '0' | string;
    message: string;
    data?: T | undefined;
}
export type PromiseResponseAPI<T = undefined> = Promise<ResponseAPIType<T>>;

/**
 * @desc 用户信息 localStorage key
 */
export const LOCALSTORAGE_USERINFO_KEY = 'S1_USERINFO';

/**
 * @desc JWT(Json Web Token) localStorage key
 */
export const LOCALSTORAGE_JWT_KEY = 'S1_JWT';

/**
 * @desc WebSocket ClientId
 */
export const LOCALSTORAGE_PERMISSION_KEY = 'S1_PERMISSIONS';

/**
 * @desc 数据分析配置
 */
export const LOCALSTORAGE_ANALYSIS_CONFIG_DATA = 's1_ANALYSIS_CONFIG';

/**
 * @desc 数据分析配置SNs
 */
export const LOCALSTORAGE_ANALYSIS_CONFIG_PRO_DATA = 's1_ANALYSIS_PRO_CONFIG';
export const LOCALSTORAGE_ANALYSIS_CONFIG_SNS_DATA = 's1_ANALYSIS_SNS_CONFIG';

/**
 * 所选国家对应的站点
 */
export const LOCALSTORAGE_SELECTED_SITE = 's1_SELECTED_SITE';

/**
 * 登录注册，所选国家
 */
export const LOCALSTORAGE_SELECTED_COUNTRY = 's1_SELECTED_COUNTRY';

/**
 * @desc WebSocket ClientId
 */
export const LOCALSTORAGE_PRODUCT_TYPE_KEY = 'S1_PRODUCT_TYPE';

/**
 * @desc System Language
 */
export const LOCALSTORAGE_LANG_KEY = 'S1_LANG';

/**
 * @desc 进入系统限制是否有选择过产品
 */
export const FIRST_SHOW_SWITCH_SYSTEM = 'FIRST_SHOW_SWITCH_SYSTEM';

/**
 * @desc 用户信息 类型
 */
export type UserInfoType = {
    userId: string; // 用户 id
    email: string; // 登录名 email
    enterpriseId: string; // 企业 id
    icon: string;
    userName: string;
    roleIds?: number[];
    userType?: 0 | 1; // 0-企业注册，1-员工注册
};

/**
 * @description 获取角色枚举值映射函数
 * @returns ROLE_NAME_CODE
 */
export const Get_ROLE_NAME_CODE = (): { [key: number]: string } => ({
    [Role_Id.EF_GLY]: t`EF administrator`, // EF 管理员
    [Role_Id.EF_YG]: t`EF staff`, // EF员工
    [Role_Id.FWS]: t`Installer`, // 安装商
    [Role_Id.FWS_GLY]: t`Administrator`, // 服务商管理员
    [Role_Id.FWS_TYG]: t`Experience`, // 体验官
    [Role_Id.FWS_AZY]: t`Installer staff`, // 安装员
    [Role_Id.FWS_YW]: t`Operation and maintenance`, // 运维
    [Role_Id.DLS_GLY]: t`web_ef_register_agent`, // 代理商
    [Role_Id.SUNNOVA]: t`SUNNOVA`,
});

export const defaultSystemName = 'PowerOcean';

export const GOOGLE_MAP_KEY = 'AIzaSyAEY5R902_gyPQQ8QqJiDe-rpu2x6naPDw';

export const UNIT_MAP: { [key: string]: MessageDescriptor } = {
    kWh: defineMessage({ message: 'kWh' }),
};

// 设备状态
export const DEVICE_STATUS = {
    Online: defineMessage({ message: 'Online' }),
    Offline: defineMessage({ message: 'Offline' }),
};

// 货币枚举
export const MONETARY_UNIT: { [key: string]: string } = {
    CNY: 'CNY ￥',
    USD: 'USD ＄',
    EUR: 'EUR €',
    GBP: 'GBP ￡',
    AUD: 'AUD ＄',
};

export const COMPANY_INFO = `Copyright © ${new Date().getFullYear()} EcoFlow All Rights Reserved.`;

/**
 * @desc Mqtt 用户版随机 32 对密钥
 */
export const KEY_SECRET = [
    ['e80b6010a485434a806e5e531479a37c', 'aed8900fd005458cb41a762ffe375e1b'],
    ['c36fba460766448f892fb1d10ec9a887', '2e44ccd5180941f69e305189210658fa'],
    ['2bd767c721c04e9da13580e7865dba10', '64c51fefca384bd3af7a780cd1c19715'],
    ['c71d7eaf92f54d978993b06749b23f8d', '7ae9e4834af54fbda072821b379162cc'],
    ['675ca589c2dd4e0f85559a12d1b8aa0a', 'd42c990ed1614b318a1c62d55b2fbcdb'],
    ['6e6f462422644ed9bf86081160d26026', '2c56c937ecf44a86a690a7072ee73809'],
    ['3b7d21606a23480dacfa3616c01d645d', 'b38e858e4a6c47f79503ad4f93eb58a0'],
    ['bf8fb28e329649dea068441b6a605742', '921273f0a8e249d388c5aa30ff4b05da'],
    ['273dba38ae724783a22641b2495f7077', '39ec9138f14748d6af4dca88339087fe'],
    ['9675fcfaf74a4eb488992dabd0786073', 'e3581d5cb76f447596f50a63f2dc9949'],
    ['87f8d63eaa604149a23e3561b2e06e2b', '2d29de38431146a0bdca289d1f4d146e'],
    ['b3a553c2424e4a0e9083b6ee97f8d538', '47ad0dbd3a7d46b8a0bd9c7f04cceceb'],
    ['79e7410c36354130aecfc714451f43d3', '2d8cdfc12da544fe93b0aa7c3ea5bab8'],
    ['b037d78184f5442a959c70ae92fcb4f4', '888ae575907f4b0eafbd9606520635bc'],
    ['5d0d3683884d48fd9449f5fedddc9725', 'abb484e211cc4c58a2b735f0aed09ff5'],
    ['12a0dd6c74c642268c44cf5c9fdd4397', 'd7c2668c5f054617bee886c744c818ee'],
    ['687c0db8f1064542a597a4629a81f0fd', '72b9ffe730b84f0e96dabd7164198549'],
    ['0521ba1633e4441c88020ed9640a8bd3', '7014eff3e00b42b7af257c3db8f3bb8d'],
    ['d3053164fde24dbdb5681bddc1fce936', '1beb9d0033974e78acd832ccc8a428eb'],
    ['72d5fe77efa6415db3373e3798e5107b', '1ed248b5d7304cc58c2f8a1ab1e83bc8'],
    ['71a22a54960e4f57aa92d88c6ea390de', '2cb1af3a6bc64d66b20eaf6a86ff6c25'],
    ['3041d0ff3e494b30a3faecc54af6d1a9', '4fc85aef548843d1bcdae60e72ac34f7'],
    ['a2ab845e94924c85898c4c21a9bd29d9', 'db3df7955ff749c4b3601088afb39bb1'],
    ['795d8b7fa08b4f6a9174d20548109a58', '3c63b21e0efb414f901436e481cfed4e'],
    ['1f6d87c3efe744f3965f44a2e805f15d', '34ab2574b87b4fd3b10fbb2f4198b1c8'],
    ['ec00f2a6d4d24b57904c07219973c8c0', 'f700ab1d9c164635a8db3499f5de692f'],
    ['b41c58a3073a42cc8887fe8839857e2b', '2c6140898c3e48f78f4ed09eee6a78c3'],
    ['102a7727f9564f969f98d3925b5626c5', '5c5190512e204fb69f3d69d7448eb4be'],
    ['3eca576d1bbe488ca40770b4ba15ba6c', 'c61e14165d464b13bdb438caf80e240c'],
    ['5c664f634f1344af8084d356a3bbae51', '305685a24d764c57b29a97f690edc788'],
    ['a585293382a24cdab24236d2f4410408', '7f6e7d3bd50b4d6b8030bbed4447edbd'],
    ['1f7a3710538444c08af20618fa06863a', '62d78b3799554080a24814b98492f753'],
];

// 时区
export const UTC_MAP: { [key: string]: string } = {
    '-11': 'UTC-11:00',
    ' -10': 'UTC-10:00',
    '-9': 'UTC-09:00',
    '-8': 'UTC-08:00',
    '-7': 'UTC-07:00',
    '-6': 'UTC-06:00',
    '-5': 'UTC-05:00',
    '-4': 'UTC-04:00',
    '-3': 'UTC-03:00',
    '-2': 'UTC-02:00',
    '-1': 'UTC-01:00',
    0: 'UTC+0:00',
    1: 'UTC+01:00',
    2: 'UTC+02:00',
    3: 'UTC+03:00',
    4: 'UTC+04:00',
    5: 'UTC+05:00',
    6: 'UTC+06:00',
    7: 'UTC+07:00',
    8: 'UTC+08:00',
    9: 'UTC+09:00',
    10: 'UTC+10:00',
    11: 'UTC+11:00',
    12: 'UTC+12:00',
};

export type OPENED_COUNTRIES_TYPE = {
    [key in 'eu' | 'us']?: CountryInfo[];
};

export const GET_OPENED_COUNTRIES = () => ({
    eu: [
        {
            countryId: 82,
            countryCode: 'DE',
            countryName: t`Germany`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 105,
            countryCode: 'IE',
            countryName: t`Ireland`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 69,
            countryCode: 'EE',
            countryName: t`Estonia`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 6,
            countryCode: 'AD',
            countryName: `Andorra`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 15,
            countryCode: 'AT',
            countryName: t`Austria`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 21,
            countryCode: 'BY',
            countryName: t`Belarus`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 34,
            countryCode: 'BG',
            countryName: t`Bulgaria`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 22,
            countryCode: 'BE',
            countryName: t`Belgium`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 100,
            countryCode: 'IS',
            countryName: t`Iceland`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 176,
            countryCode: 'PL',
            countryName: t`Poland`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 59,
            countryCode: 'DK',
            countryName: t`Denmark`,
            dataCenterCode: 'eu',
        },
        // {
        //     countryId: 182,
        //     countryCode: 'RU',
        //     countryName: t`Russia`,
        //     dataCenterCode: 'rs',
        //
        // },
        {
            countryId: 75,
            countryCode: 'FR',
            countryName: t`France`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 74,
            countryCode: 'FI',
            countryName: t`Finland`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 156,
            countryCode: 'NL',
            countryName: t`Netherlands`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 147,
            countryCode: 'ME',
            countryName: t`Montenegro`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 58,
            countryCode: 'CZ',
            countryName: t`Czech Republic`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 55,
            countryCode: 'HR',
            countryName: t`Croatia`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 120,
            countryCode: 'LV',
            countryName: t`Latvia`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 126,
            countryCode: 'LT',
            countryName: t`Lithuania`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 125,
            countryCode: 'LI',
            countryName: t`Liechtenstein`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 127,
            countryCode: 'LU',
            countryName: t`Luxembourg`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 181,
            countryCode: 'RO',
            countryName: t`Romania`,
            dataCenterCode: 'eu',
        },
        // {
        //     countryId: 135,
        //     countryCode: 'MT',
        //     countryName: '马耳他',
        //     dataCenterCode: 'eu',
        //
        // },

        // {
        //     countryId: 144,
        //     countryCode: 'MD',
        //     countryName: '摩尔多瓦',
        //     dataCenterCode: 'eu',
        //
        // },
        // {
        //     countryId: 145,
        //     countryCode: 'MC',
        //     countryName: '摩纳哥',
        //     dataCenterCode: 'eu',
        //
        // },
        {
            countryId: 165,
            countryCode: 'NO',
            countryName: t`Norway`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 177,
            countryCode: 'PT',
            countryName: t`Portugal`,
            dataCenterCode: 'eu',
        },

        {
            countryId: 213,
            countryCode: 'SE',
            countryName: t`Sweden`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 214,
            countryCode: 'CH',
            countryName: t`Switzerland`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 196,
            countryCode: 'RS',
            countryName: t`Serbia`,
            dataCenterCode: 'eu',
        },
        // {
        //     countryId: 57,
        //     countryCode: 'CY',
        //     countryName: '塞浦路斯',
        //     dataCenterCode: 'sp',
        //
        // },

        {
            countryId: 200,
            countryCode: 'SK',
            countryName: t`Slovakia`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 201,
            countryCode: 'SI',
            countryName: t`Slovenia`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 230,
            countryCode: 'UA',
            countryName: t`Ukraine`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 207,
            countryCode: 'ES',
            countryName: t`Spain`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 85,
            countryCode: 'GR',
            countryName: t`Greece`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 99,
            countryCode: 'HU',
            countryName: t`Hungary`,
            dataCenterCode: 'eu',
        },
        // {
        //     countryId: 12,
        //     countryCode: 'AM',
        //     countryName: '亚美尼亚',
        //     dataCenterCode: 'sp',
        //
        // },
        {
            countryId: 107,
            countryCode: 'IT',
            countryName: t`Italy`,
            dataCenterCode: 'eu',
        },
        {
            countryId: 232,
            countryCode: 'GB',
            countryName: t`Britain`,
            dataCenterCode: 'eu',
        },
        // {
        //     countryId: 84,
        //     countryCode: 'GI',
        //     countryName: '直布罗陀',
        //     dataCenterCode: 'eu',
        //
        // },
        // {
        //     countryId: 3,
        //     countryCode: 'AL',
        //     countryName: '阿尔巴尼亚',
        //     dataCenterCode: 'eu',
        //
        // },
    ],
    us: [
        {
            countryId: 233,
            countryCode: 'US',
            countryName: t`America`,
            dataCenterCode: 'us',
        },
        // {
        //     countryId: 109,
        //     countryCode: 'JP',
        //     countryName: t`Japan`,
        //     dataCenterCode: 'jp',
        // },
        {
            countryId: 39,
            countryCode: 'CA',
            countryName: t`Canada`,
            dataCenterCode: 'us',
        },
        {
            countryId: 142,
            countryCode: 'MX',
            countryName: t`Mexico`,
            dataCenterCode: 'us',
        },
        // {
        //     countryId: 192,
        //     countryCode: 'SM',
        //     countryName: '圣马力诺',
        //     dataCenterCode: 'us',
        //
        // },
    ],
});

// 多语言词条版本生产环境地址
export const remoteVersionSrc = {
    'local.dev': 'https://api-translation.ecoflow.com/version/test/entry?name=Web',
    test: 'https://api-translation.ecoflow.com/version/test/entry?name=Web',
    // test: 'https://fe-static-prod-cn.ecoflow.com/translation/version/Web/production/latest.json',
    prod: 'https://fe-static-prod-cn.ecoflow.com/translation/version/Web/production/latest.json',
};
